import React, { useMemo, useState } from 'react';
import Modal from '../common/Modal';
import Table from '../common/Table';
import Button from '../common/Button';
import SearchBar from '../common/SearchBar';
import BusinessPermitApplicationModal from './NewBusinessPermitModal';
import ViewApplicationModal from './ViewApplicationModal';
import {
  ArrowDownTrayIcon,
  EyeIcon,
  FunnelIcon,
  ArrowPathRoundedSquareIcon,
} from '@heroicons/react/24/outline';
import { useFetch } from '../../hooks/useCallApi';
import { businessPermitsAPI } from '../../services/api/admin/businessPermitApi';
import { showSuccess, showError } from '../../utils/notificationHelper';

const RenewalExpiryMonitoringModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('expiring');
  const [searchValue, setSearchValue] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [applicationMode, setApplicationMode] = useState('create');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [renewApplicationId, setRenewApplicationId] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [filters, setFilters] = useState({
    permitYearFrom: '',
    permitYearTo: '',
    expirationFrom: '',
    expirationTo: '',
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 5,
    total: 0,
    lastPage: 1,
  });

  const params = useMemo(
    () => ({
      section: activeTab === 'expiring' ? 'expiring_permit' : 'expired_permit',
      per_page: pagination.perPage,
      page: pagination.currentPage,
      search: searchValue || undefined,
    }),
    [activeTab, filters, pagination.currentPage, pagination.perPage, searchValue]
  );

  const { data: response, isLoading } = useFetch(
    ['business-permits-tab-view', params],
    () => businessPermitsAPI.getAllExpiringPermits(params),
    { keepPreviousData: true, staleTime: 30 * 1000 }
  );

  /**
   * Backend `tab-view` shape:
   * { data: { business_permits: [...], meta: { current_page, last_page, per_page, total } } }
   *
   * UI/table expects rows that contain (at least):
   * - `id` used for expiring/expired by-id endpoints (permit id)
   * - `permit_number`, `permit_year`, `remaining_term` / `expired_date`
   * - optional `business.name` (fallbacks allowed)
   */
  const rawRows = (() => {
    const candidate =
      response?.data?.business_permits ??
      response?.business_permits ??
      response?.data?.data?.business_permits ??
      response?.data?.data ??
      response?.data ??
      response;

    return Array.isArray(candidate) ? candidate : [];
  })();

  const tableData = useMemo(() => {
    return rawRows.map((item) => {
      const permit = item?.business_permit ?? {};

      return {
        // IMPORTANT: keep `id` as the PERMIT id for the by-id endpoints.
        id: permit?.id ?? item?.id,

        // Keep application id available for downstream flows if needed.
        permit_application_id: item?.id,
        application_ref_no: item?.application_ref_no,
        application_type: item?.application_type,
        permit_year: item?.permit_year ?? permit?.permit_year ?? null,
        status: item?.status,

        // Table columns
        permit_number: permit?.permit_number ?? item?.application_ref_no ?? "—",
        remaining_term: permit?.remaining_term ?? "—",
        expired_date: permit?.expired_date ?? "—",

        // Optional business info; backend sample doesn't include it.
        business: item?.business ?? item?.application?.business ?? null,

        // Keep full nested permit if needed by consumers.
        business_permit: permit,
      };
    });
  }, [rawRows]);

  const meta =
    response?.data?.meta ??
    response?.meta ??
    response?.data?.data?.meta ??
    null;

  const totalItems = meta?.total ?? response?.total ?? tableData.length;
  const lastPage =
    meta?.last_page ??
    response?.last_page ??
    Math.max(1, Math.ceil(totalItems / pagination.perPage));

  const handlePageChange = (page, perPage) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
      perPage: perPage || prev.perPage,
    }));
  };

  const handleViewBusiness = async (row) => {
    const id = row.id;
    if (id == null) {
      showError('Cannot load permit details');
      return;
    }
    setViewLoading(true);
    try {
      const apiCall =
        activeTab === 'expiring'
          ? businessPermitsAPI.getBusinessPermitExpiringById(id)
          : businessPermitsAPI.getBusinessPermitExpiredById(id);
      const res = await apiCall;
      const raw = res?.data?.data ?? res?.data;
      if (!raw) {
        showError('Failed to load permit details');
        return;
      }

      // Normalize backend response so ViewApplicationModal fields populate.
      // Some endpoints return { application, business, business_permit }, others return application-like objects directly.
      const coreApplication = raw?.application ?? raw;
      const businessPermit = raw?.business_permit ?? raw?.businessPermit ?? raw?.permit ?? raw?.business_permit ?? raw?.business_permit ?? raw?.business_permit;

      const business =
        coreApplication?.business ??
        raw?.business ??
        raw?.application?.business ??
        null;

      const forView = {
        ...coreApplication,
        business: business
          ? {
              ...business,
              // Ensure both `name` and `business_name` exist for various viewers.
              name: business?.name ?? business?.business_name,
              business_name: business?.business_name ?? business?.name,
            }
          : undefined,
        business_permit: businessPermit ?? raw?.business_permit ?? raw,
        audit_logs: raw?.audit_logs ?? coreApplication?.audit_logs,
        documents: coreApplication?.documents ?? raw?.documents,
        endorsement: coreApplication?.endorsement ?? raw?.endorsement,
      };
      setSelectedBusiness(forView);
      setShowViewModal(true);
    } catch (err) {
      console.error('Error fetching permit:', err);
      showError(err?.response?.data?.message ?? 'Failed to load permit details');
    } finally {
      setViewLoading(false);
    }
  };

  const handleRenewBusiness = async (row) => {
    const id = row?.id;
    if (id == null) {
      showError('Cannot load permit details for renewal');
      return;
    }

    setViewLoading(true);
    try {
      const apiCall =
        activeTab === 'expiring'
          ? businessPermitsAPI.getBusinessPermitExpiringById(id)
          : businessPermitsAPI.getBusinessPermitExpiredById(id);
      const res = await apiCall;
      const raw = res?.data?.data ?? res?.data;
      const coreApplication = raw?.application ?? raw;

      const permitApplicationId =
        raw?.application?.id ??
        raw?.application_id ??
        raw?.permit_application_id ??
        coreApplication?.id ??
        coreApplication?.permit_application_id ??
        null;

      if (!raw || !permitApplicationId) {
        showError('Failed to load permit details for renewal');
        return;
      }

      setApplicationMode('renewal');
      setSelectedBusiness(raw);
      setRenewApplicationId(permitApplicationId);
      setShowApplicationModal(true);
    } catch (err) {
      console.error('Error fetching permit for renewal:', err);
      showError(err?.response?.data?.message ?? 'Failed to load permit details for renewal');
    } finally {
      setViewLoading(false);
    }
  };

  const handleApplicationSubmit = (data) => {
    console.log('Application submitted:', data);
    setShowApplicationModal(false);
    setSelectedBusiness(null);
  };

  const handleExport = async () => {
    try {
      const params = {
        section: activeTab === 'expiring' ? 'expiring_permit' : 'expired_permit',
        search: searchValue || undefined,
      };

      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await businessPermitsAPI.exportExpiringPermits(params);

      // Create a download link for the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Set filename based on current tab
      const filename = activeTab === 'expiring'
        ? 'expiring-business-permits.csv'
        : 'expired-business-permits.csv';

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess(`${activeTab === 'expiring' ? 'Expiring' : 'Expired'} business permits exported successfully`);
    } catch (error) {
      console.error('Error exporting permits:', error);
      showError('Failed to export permits');
    }
  };

  const columns = useMemo(() => {
    const base = [
      { key: 'permit_number', label: 'REFERENCE NO.' },
      {
        key: 'business.name',
        label: 'BUSINESS NAME',
        render: (_, row) =>
          row.business?.name ??
          row.business?.business_name ??
          '—',
      },
      { key: 'permit_year', label: 'PERMIT YEAR' },
    ];

    if (activeTab === 'expiring') {
      base.push({ key: 'remaining_term', label: 'REMAINING TERM' });
    } else {
      base.push({ key: 'expired_date', label: 'DATE EXPIRED' });
    }

    base.push({
      key: 'actions',
      label: 'ACTIONS',
      render: (_, row) => (
        <div className="flex items-center justify-end space-x-1">
          <button
            className="text-blue-600 hover:text-blue-900 p-1.5 rounded-full hover:bg-blue-50 transition-all duration-200 hover:scale-110 disabled:opacity-50 disabled:pointer-events-none"
            type="button"
            aria-label="View"
            title="View Details"
            onClick={() => handleViewBusiness(row)}
            disabled={viewLoading}
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            className="text-green-600 hover:text-green-900 p-1.5 rounded-full hover:bg-green-50 transition-all duration-200 hover:scale-110"
            type="button"
            aria-label="Renew"
            title="Renew"
            onClick={() => handleRenewBusiness(row)}
          >
            <ArrowPathRoundedSquareIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    });

    return base;
  }, [activeTab, viewLoading, handleViewBusiness, handleRenewBusiness]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Renewal & Expiry Business Permit" size="5xl">
      <div className="space-y-4">
        {/* Tabs */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-10 border-b border-gray-200 w-full">
            <button
              type="button"
              onClick={() => {
                setActiveTab('expiring');
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
              className={`py-3 px-2 text-sm font-medium ${activeTab === 'expiring' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'
                }`}
            >
              Expiring Business Permits
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('expired');
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
              className={`py-3 px-2 text-sm font-medium ${activeTab === 'expired' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'
                }`}
            >
              Expired Business Permits
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-[28rem]">
              <SearchBar
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  setPagination((prev) => ({ ...prev, currentPage: 1 }));
                }}
                placeholder="Search businesses by name, reference no..."
              />
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="flex items-center gap-2 px-5 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition"
            >
              <FunnelIcon className="h-5 w-5 text-gray-500" />
              <span>Filters</span>
            </button>
          </div>
          <Button
            variant="primary"
            icon={<ArrowDownTrayIcon className="h-5 w-5" />}
            onClick={handleExport}
          >
            Export
          </Button>
        </div>

        {/* Filters panel */}
        {filtersOpen ? (
          <div className="border border-gray-200 rounded-xl p-4 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {activeTab === 'expired' ? (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expiration Date</label>
                  <div className="flex items-center gap-2">
                    <input
                      value={filters.expirationFrom}
                      onChange={(e) => setFilters((prev) => ({ ...prev, expirationFrom: e.target.value }))}
                      placeholder="dd/mm/yyyy"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    <span className="text-gray-400">-</span>
                    <input
                      value={filters.expirationTo}
                      onChange={(e) => setFilters((prev) => ({ ...prev, expirationTo: e.target.value }))}
                      placeholder="dd/mm/yyyy"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              ) : null}

              <div className={activeTab === 'expired' ? 'md:col-span-2' : 'md:col-span-3'}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Permit Year</label>
                <div className="flex items-center gap-2">
                  <input
                    value={filters.permitYearFrom}
                    onChange={(e) => setFilters((prev) => ({ ...prev, permitYearFrom: e.target.value }))}
                    placeholder="yyyy"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    inputMode="numeric"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    value={filters.permitYearTo}
                    onChange={(e) => setFilters((prev) => ({ ...prev, permitYearTo: e.target.value }))}
                    placeholder="yyyy"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    inputMode="numeric"
                  />
                </div>
              </div>

              <div className="flex items-end gap-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setFilters({
                      permitYearFrom: '',
                      permitYearTo: '',
                      expirationFrom: '',
                      expirationTo: '',
                    });
                    setPagination((prev) => ({ ...prev, currentPage: 1 }));
                    setFiltersOpen(false);
                  }}
                >
                  Clear
                </Button>
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={() => {
                    setPagination((prev) => ({ ...prev, currentPage: 1 }));
                    setFiltersOpen(false);
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Table */}
        <Table
          columns={columns}
          data={tableData}
          getRowKey={(row) => row.id ?? row.permit_number}
          pagination={{
            currentPage: pagination.currentPage,
            perPage: pagination.perPage,
            total: totalItems,
            lastPage,
          }}
          onPageChange={handlePageChange}
          paginationProps={{
            showPerPage: true,
            perPageOptions: [5, 10, 15],
            isLoading,
          }}
          isLoading={isLoading}
        />
      </div>

      {/* Business Permit Application Modal for Edit/Renewal */}
      <BusinessPermitApplicationModal
        isOpen={showApplicationModal}
        onClose={() => {
          setShowApplicationModal(false);
          setSelectedBusiness(null);
          setRenewApplicationId(null);
        }}
        mode={applicationMode}
        initialData={selectedBusiness}
        renewApplicationId={renewApplicationId}
        onSubmit={handleApplicationSubmit}
      />

      {/* View Application Modal */}
      <ViewApplicationModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedBusiness(null);
        }}
        application={selectedBusiness}
      />
    </Modal>
  );
};

export default RenewalExpiryMonitoringModal;

